/**
 * «Сайты» раздела EO «Виджет» (01.11, ТЗ 1.17 EMB-019 ч. 1): список сайтов с ключом и состоянием v2, создание сайта (ключ v2 —
 * 32 случайных байта, создаёт V4: POST /api/widget-sites), замок «МК переведён в V4» (widget_v2_mk_migrated: пока не отмечено,
 * включить v2 нельзя — INTERNAL 0.5 §8, Р-9). Карточка сайта — WidgetSiteCard. Ключ публичен (SEC-016) — целиком в карточке
 * и в коде установки.
 */
import { useState } from "react";
import { api } from "../../../api/client";
import { ConfirmModal } from "../../../components/shared";
import { BASE, failText, useLoad } from "./widgetApi";
import { ago, orenTime, shortKey } from "./widgetFmt";
import type { Overview } from "./widgetTypes";
import { WidgetSiteCard } from "./WidgetSiteCard";
import css from "./widget.module.css";

export function WidgetSites({ siteId, onSite, onInstall }: { siteId: number | null; onSite: (id: number | null) => void; onInstall: (id: number) => void }) {
  const ov = useLoad<Overview>(`${BASE}/v2/overview`);
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [ask, setAsk] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const now = Date.now();

  const create = async () => {
    const d = domain.trim().toLowerCase();
    if (!d || busy) return;
    setBusy(true); setToast("");
    try {
      await api(BASE, { method: "POST", body: { domain: d } });
      setDomain(""); setToast(`Сайт ${d} создан, ключ выдан. Дальше — конфиг v2 в карточке сайта.`); ov.reload();
    } catch (e) { setToast(failText(e, "Не удалось создать сайт")); }
    setBusy(false);
  };
  const setMk = async (migrated: boolean) => {
    setAsk(null); setBusy(true); setToast("");
    try { await api(`${BASE}/v2/mk-migrated`, { method: "PUT", body: { migrated } }); setToast(migrated ? "Отмечено: МК переведён в V4" : "Отметка снята"); ov.reload(); }
    catch (e) { setToast(failText(e, "Не удалось сменить отметку")); }
    setBusy(false);
  };

  if (open && siteId !== null) return <WidgetSiteCard id={siteId} onBack={() => { setOpen(false); ov.reload(); }} onInstall={() => onInstall(siteId)} />;
  const sites = ov.data?.sites ?? [];
  return (
    <div className={css.card}>
      <div className={css.head}>
        <span className={css.title}>Сайты виджета v2</span>
        <button type="button" className={css.ghost} disabled={ov.busy} onClick={ov.reload}>Обновить</button>
      </div>
      {ov.err ? <div className={css.err}>{ov.err}</div> : null}
      {ov.data ? (
        <div className={css.line}>
          <span className={`${css.badge} ${ov.data.mk_migrated ? css.ok : css.warn}`}>{ov.data.mk_migrated ? "МК переведён в V4" : ov.data.mk_readable ? "МК ещё в V1" : "отметка не читается"}</span>
          <span className={css.muted}>{ov.data.mk_migrated ? "ключи v2 можно включать" : "пока МК продаёт через V1, включить v2 нельзя: возможна двойная продажа слота"}</span>
          <button type="button" className={css.ghost} disabled={busy} onClick={() => setAsk(!ov.data?.mk_migrated)}>{ov.data.mk_migrated ? "Снять отметку" : "Отметить «МК переведён»"}</button>
        </div>
      ) : null}
      <div className={css.line}>
        <input className={css.input} value={domain} placeholder="домен, например unihorror.ru" aria-label="Домен нового сайта" autoComplete="off"
          spellCheck={false} onChange={(e) => setDomain(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void create(); }} />
        <button type="button" className={css.btn} disabled={busy || !domain.trim()} onClick={() => void create()}>Новый сайт</button>
      </div>
      {toast ? <div className={css.toast} role="status">{toast}</div> : null}
      {!ov.data && !ov.err ? <div className={css.muted}>Загружаю…</div> : null}
      {ov.data && !sites.length ? <div className={css.muted}>Сайтов ещё нет</div> : null}
      {sites.map((s) => (
        <div key={s.id} className={css.item}>
          <div className={css.line}>
            <b>{s.domain}</b>
            <span className={`${css.badge} ${!s.active ? css.fail : s.v2 && s.enabled ? css.ok : css.none}`}>{!s.active ? "ключ отозван" : !s.v2 ? "v2 не настроен" : s.enabled ? "v2 включён" : "v2 выключен"}</span>
            {s.v2 && !s.config_ok ? <span className={`${css.badge} ${css.fail}`}>конфиг не по схеме</span> : null}
            {s.disabled ? <span className={`${css.badge} ${css.warn}`}>выключатель: запасной путь</span> : null}
            {s.v2 ? <span className={`${css.badge} ${s.pd ? css.ok : css.warn}`}>{s.pd ? `согласие ${s.pd.version}` : "нет текста согласия"}</span> : null}
          </div>
          <span className={css.meta}>ключ {shortKey(s.key)} · №{s.id}{s.v2 ? ` · конфиг v${s.config_version}` : ""} · последняя заявка: {s.last_request_at ? `${orenTime(s.last_request_at)} (${ago(s.last_request_at, now)})` : "нет данных"}</span>
          <div className={css.line}>
            <button type="button" className={css.btn} onClick={() => { onSite(s.id); setOpen(true); }}>Открыть</button>
            {s.v2 ? <button type="button" className={css.ghost} onClick={() => onInstall(s.id)}>Код установки</button> : null}
          </div>
        </div>
      ))}
      {ask !== null ? (
        <ConfirmModal tone="warn" title={ask ? "Отметить «МК переведён в V4»?" : "Снять отметку «МК переведён»?"} actionLabel={ask ? "Отметить" : "Снять"}
          onAction={() => void setMk(ask)} onCancel={() => setAsk(null)}>
          {ask ? "Отмечать, только когда Мир Квестов продаёт слоты через V4 (или сделаны OPS-002/003): иначе включённый v2 даст двойную продажу слота."
            : "Новые включения v2 станут невозможны; уже включённые сайты продолжат работать."}
        </ConfirmModal>
      ) : null}
    </div>
  );
}
